import { useState } from "react";
import { motion } from "framer-motion";
import Seo from "@/components/Seo";
import { MENU_CATEGORIES, BREAKFAST_ITEMS, SWEET_ITEMS, IMG } from "@/i18n";
import { scrollToId } from "@/hooks/useLenis";

const EASE = [0.22, 1, 0.36, 1];

const Reveal = ({ children, delay = 0, ...rest }) => (
  <motion.div
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.7, ease: EASE, delay }}
    {...rest}
  >
    {children}
  </motion.div>
);

const PriceRow = ({ name, price, lang }) => (
  <li className="flex items-baseline gap-3 border-b border-black/5 py-3.5">
    <span className="text-[15px] font-medium text-ink/85">{name[lang]}</span>
    <span className="flex-1 border-b border-dotted border-black/10" />
    <span className="font-display text-sm font-bold text-branddark">{price}</span>
  </li>
);

const TABS = [
  { id: "menu-breakfast", key: "breakfast" },
  { id: "menu-lunch", key: "lunch" },
  { id: "menu-sweets", key: "sweets" },
  { id: "menu-drinks", key: "drinks" },
];

const SUMMARY = [
  { to: "/breakfast", img: IMG.catBreakfast, key: "breakfast", count: BREAKFAST_ITEMS.length },
  { to: "/lunch", img: IMG.catLunch, key: "lunch", count: MENU_CATEGORIES.filter((c) => c.id !== "drinks").reduce((n, c) => n + c.items.length, 0) },
  { to: "/sweets", img: IMG.sweets, key: "sweets", count: SWEET_ITEMS.length },
  { to: "#menu-drinks", img: IMG.drinks, key: "drinks", count: MENU_CATEGORIES.find((c) => c.id === "drinks").items.length },
];

export default function MenuPage({ t, lang }) {
  const m = t.menuPage;
  const [active, setActive] = useState("menu-breakfast");

  const lunchCats = MENU_CATEGORIES.filter((c) => c.id !== "drinks");
  const drinks = MENU_CATEGORIES.find((c) => c.id === "drinks");

  return (
    <main data-testid="menu-page">
      <Seo title="Full Menu — Kurkuma AI Kitchen" siteName="Kurkuma AI Kitchen" description={m.sub} />

      <section className="relative overflow-hidden bg-navy text-cream" data-testid="menu-hero">
        <img src={IMG.heroBowl} alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="relative mx-auto max-w-7xl px-4 py-16 md:px-8 md:py-20">
          <motion.div initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
            <h1 className="font-display text-4xl font-bold tracking-tight md:text-5xl" data-testid="menu-heading">{m.title}</h1>
            <p className="mt-2 text-cream/70">{m.sub}</p>
          </motion.div>
          <div className="mt-8 flex flex-wrap gap-2" role="tablist" aria-label="Menu categories">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                role="tab"
                aria-selected={active === tab.id}
                data-testid={`menu-tab-${tab.key}`}
                onClick={() => {
                  setActive(tab.id);
                  scrollToId(tab.id);
                }}
                className={`rounded-full px-6 py-2.5 text-sm font-semibold transition-colors ${
                  active === tab.id ? "bg-brand text-navy" : "border border-cream/25 text-cream/80 hover:border-brand hover:text-brand"
                }`}
              >
                {m.tabs[tab.key]}
              </button>
            ))}
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-14 md:px-8">
        {/* breakfast */}
        <section id="menu-breakfast" data-testid="menu-section-breakfast" className="scroll-mt-28">
          <Reveal>
            <h2 className="font-display text-2xl font-bold text-navy">{m.tabs.breakfast}</h2>
            <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.25em] text-ink/40">{t.breakfastPage.hours}</p>
            <ul className="mt-6">
              {BREAKFAST_ITEMS.map((item) => (
                <PriceRow key={item.id} name={item.name} price={item.price} lang={lang} />
              ))}
            </ul>
          </Reveal>
        </section>

        {/* lunch categories */}
        <section id="menu-lunch" data-testid="menu-section-lunch" className="mt-16 scroll-mt-28">
          <Reveal>
            <h2 className="font-display text-2xl font-bold text-navy">{m.tabs.lunch}</h2>
            <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.25em] text-ink/40">{t.lunchPage.hours}</p>
          </Reveal>
          <div className="mt-4 grid grid-cols-1 gap-x-12 md:grid-cols-2">
            {lunchCats.map((cat) => (
              <Reveal key={cat.id}>
                <h3 className="mt-8 font-display text-base font-semibold uppercase tracking-widest text-branddark">
                  {cat.label[lang]}
                </h3>
                <ul className="mt-2">
                  {cat.items.map((item) => (
                    <PriceRow key={item.name.en} name={item.name} price={item.price} lang={lang} />
                  ))}
                </ul>
              </Reveal>
            ))}
          </div>
        </section>

        {/* sweets */}
        <section id="menu-sweets" data-testid="menu-section-sweets" className="mt-16 scroll-mt-28">
          <Reveal>
            <h2 className="font-display text-2xl font-bold text-navy">{m.tabs.sweets}</h2>
            <ul className="mt-6">
              {SWEET_ITEMS.map((item) => (
                <PriceRow key={item.id} name={item.name} price={item.price} lang={lang} />
              ))}
            </ul>
          </Reveal>
        </section>

        {/* drinks */}
        <section id="menu-drinks" data-testid="menu-section-drinks" className="mt-16 scroll-mt-28">
          <Reveal>
            <h2 className="font-display text-2xl font-bold text-navy">{m.tabs.drinks}</h2>
            <ul className="mt-6">
              {drinks.items.map((item) => (
                <PriceRow key={item.name.en} name={item.name} price={item.price} lang={lang} />
              ))}
            </ul>
          </Reveal>
        </section>
      </div>

      {/* summary cards */}
      <section className="bg-paper py-14" data-testid="menu-summary">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-6 px-4 md:px-8 lg:grid-cols-4">
          {SUMMARY.map((s, i) => (
            <Reveal key={s.key} delay={i * 0.07}>
              <a
                href={s.to}
                data-testid={`menu-summary-${s.key}`}
                className="group block overflow-hidden rounded-2xl bg-cream shadow-sm transition-shadow hover:shadow-lg"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={s.img} alt={m.tabs[s.key]} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="p-4">
                  <p className="font-display text-base font-semibold text-navy">{m.tabs[s.key]}</p>
                  <p className="text-xs text-ink/50">
                    {s.count} {m.itemsWord}
                  </p>
                </div>
              </a>
            </Reveal>
          ))}
        </div>
      </section>
    </main>
  );
}
